import { useDroppable } from '@dnd-kit/core'
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable'
import { SortableCardTile } from './CardTile'
import { Composer } from './Composer'
import type { Assignee, Card, Status } from '../lib/types'

type Props = {
  status: Status
  label: string
  accent: string
  cards: Card[]
  /** True when an assignee filter is hiding some of this column's cards. */
  filtered: boolean
  defaultAssignee: Assignee
  onAdd: (input: { title: string; assignee: Assignee; status: Status }) => void
  onOpen: (card: Card) => void
  onToggleDone: (card: Card) => void
}

export function Column({
  status,
  label,
  accent,
  cards,
  filtered,
  defaultAssignee,
  onAdd,
  onOpen,
  onToggleDone,
}: Props) {
  const { setNodeRef, isOver } = useDroppable({ id: `column:${status}` })

  return (
    <section className="flex min-w-0 flex-col">
      <div className={`h-1 rounded-full ${accent}`} />

      <header className="flex items-baseline gap-2 px-1 pt-3 pb-2">
        <h2 className="text-sm font-semibold tracking-wide text-ink uppercase">{label}</h2>
        <span className="text-xs font-medium text-faint tabular-nums">{cards.length}</span>
      </header>

      <div
        ref={setNodeRef}
        data-column={status}
        className={`flex flex-1 flex-col gap-2 rounded-xl p-1.5 transition-colors
                    ${isOver ? 'bg-teal-50' : 'bg-transparent'}`}
      >
        <SortableContext items={cards.map((c) => c.id)} strategy={verticalListSortingStrategy}>
          {cards.map((card) => (
            <SortableCardTile
              key={card.id}
              card={card}
              onOpen={onOpen}
              onToggleDone={onToggleDone}
            />
          ))}
        </SortableContext>

        {cards.length === 0 && (
          <p className="px-2 py-6 text-center text-xs text-faint">
            {filtered ? 'Nothing here for this person' : 'Nothing here yet'}
          </p>
        )}

        <Composer status={status} defaultAssignee={defaultAssignee} onAdd={onAdd} />
      </div>
    </section>
  )
}
